import ApplicationConfig from '@/lib/application-config'
import * as Constants from '@/lib/constants'
import storage from '@/lib/storage'
import store from '@/store'
import connectHttpBase from './connect-http-base'

const PATH_TOKEN = '/v1/token'
const PATH_TOKEN_REFRESH = '/v1/token/refresh'
const PATH_TOKEN_IMPERSONATE = '/v1/token/impersonate'

const V2_PATH_TOKEN = '/v2/token'
const V2_PATH_TOKEN_REFRESH = '/v2/token/refresh'
const V2_PATH_TOKEN_IMPERSONATE = '/v2/token/impersonate'
const V2_PATH_TOKEN_OAUTH_LOGIN = '/v2/token/oauth_login'

const PATH_USER = '/v1/user/'
const PATH_USER_PASSWORD = '/v1/user/password'
const PATH_USER_PASSWORD_RESET = '/v1/user/password/reset'
const PATH_USER_ICON = '/v1/user/icon'
const PATH_USER_PASSWORD_ENTERED = '/v1/user/password_entered'

const V2_PATH_USER = '/v2/user/'
const V3_PATH_USER = '/v3/user'

const PATH_SEARCH_USER_EMAIL = '/v1/search/user/email'
const PATH_SUBSCRIPTION = '/v1/subscription'
const PATH_SUBSCRIPTION_REQUEST = '/v1/subscription/request'

const PATH_STRIPE = '/v1/stripe'
const V2_PATH_STRIPE = '/v2/stripe'

const PATH_DEVICES = '/v1/devices'

const PATH_WEB_MARKRES = '/v2/web_markers'

const V3_PATH_CASTER = '/v3/caster'

const PATH_PUBLIC = '/p'

/**
 * FireTest
 */
const V1_FIRETEST = '/v1/firetest'

class ConnectManagerClient {
  constructor() {
    this.refreshTokenDaemonStarted = false
  }

  simpleSignUp(simpleSignUpRequest) {
    const endpoint = this.managerEndpoint(V2_PATH_USER)
    return connectHttpBase.getHttpClient().post(endpoint, simpleSignUpRequest)
  }

  simpleSignUpAndSignIn(simpleSignUpAndSignInRequest) {
    const endpoint = this.managerEndpoint(V3_PATH_USER + '/new')
    let request = {
      appId: ApplicationConfig.getClientId(),
      deviceId: Constants.DEVICE_ID,
      deviceManufacturer: Constants.DEVICE_MANUFACTURER,
      deviceModel: Constants.DEVICE_MODEL,
      deviceSystemVersion: Constants.DEVICE_SYSTEM_VERSION,
      email: simpleSignUpAndSignInRequest.email,
      password: simpleSignUpAndSignInRequest.password,
      secretHash: connectHttpBase.calclateSecretHash(simpleSignUpAndSignInRequest.email),
      sendMeNews: simpleSignUpAndSignInRequest.sendMeNews,
      language: simpleSignUpAndSignInRequest.language,
      zoneinfo: simpleSignUpAndSignInRequest.zoneinfo,
      onSignUpSuccessHandler: simpleSignUpAndSignInRequest.onSignUpSuccessHandler,
      region: simpleSignUpAndSignInRequest.region,
    }
    return connectHttpBase.getHttpClient().post(endpoint, request)
  }

  oAuthLogin(oAuthLoginRequest) {
    const endpoint = this.managerEndpoint(V2_PATH_TOKEN_OAUTH_LOGIN)
    return connectHttpBase.getHttpClient().post(endpoint, oAuthLoginRequest)
  }

  passwordEntered() {
    const endpoint = this.managerEndpoint(PATH_USER_PASSWORD_ENTERED)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  createPassword(newPassword) {
    const endpoint = this.managerEndpoint(PATH_USER_PASSWORD)
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint, { newPassword })
  }

  impersonate(targetUserEmail) {
    let endpoint = this.managerEndpoint(V2_PATH_TOKEN_IMPERSONATE)
    const request = { targetUserEmail }
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint, request)
  }

  login(credentials) {
    let endpoint = this.managerEndpoint(V2_PATH_TOKEN)
    let email = credentials.email
    let password = credentials.password

    let request = {
      appId: ApplicationConfig.getClientId(),
      deviceId: Constants.DEVICE_ID,
      deviceManufacturer: Constants.DEVICE_MANUFACTURER,
      deviceModel: Constants.DEVICE_MODEL,
      deviceSystemVersion: Constants.DEVICE_SYSTEM_VERSION,
      email: email,
      password: password,
      secretHash: connectHttpBase.calclateSecretHash(email),
    }
    return connectHttpBase.getHttpClient().post(endpoint, request)
  }

  // returns Promise or undefined
  refreshAccessToken() {
    const userInfo = storage.getUserInfo()
    if (!userInfo) {
      console.error('UserInfo is empty in storage')
      return undefined
    }
    const refreshToken = storage.getRefreshToken()
    if (!refreshToken) {
      console.error('refresh token is not found.')
      return undefined
    }

    const request = {
      email: userInfo.email,
      refreshToken: refreshToken,
      deviceId: Constants.DEVICE_ID,
      deviceManufacturer: Constants.DEVICE_MANUFACTURER,
      deviceModel: Constants.DEVICE_MODEL,
      deviceSystemVersion: Constants.DEVICE_SYSTEM_VERSION,
    }
    let endpoint = this.managerEndpoint(V2_PATH_TOKEN_REFRESH)
    return connectHttpBase.getHttpClient().post(endpoint, request)
  }

  startRefreshTokenDaemon() {
    if (!this.refreshTokenDaemonStarted) {
      setInterval(
        function() {
          const promise = this.refreshAccessToken()
          if (promise) {
            promise
              .then(response => {
                store.dispatch('refreshTokenSuccess', response.data)
              })
              .catch(error => {
                // FIXME
                console.error(error)
              })
          }
        }.bind(this),
        ApplicationConfig.refreshTokenIntervalSeconds
      )
    }
  }

  updateUser(userData) {
    const endpoint = this.managerEndpoint(V2_PATH_USER)
    const request = {
      nickname: userData.nickname,
      sendMeNews: userData.sendMeNews,
      language: userData.language,
      systemOfUnits: userData.systemOfUnits,
    }
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  getMe() {
    const endpoint = this.managerEndpoint(PATH_USER)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  getUserInfo(username) {
    const endpoint = this.managerEndpoint(V2_PATH_USER + username)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  resetPassword(email) {
    const endpoint = this.managerEndpoint(PATH_USER_PASSWORD_RESET)
    return connectHttpBase.getHttpClient().put(endpoint, { email }) // ログイン不要
  }

  changePassword(currentPassword, newPassword) {
    const endpoint = this.managerEndpoint(PATH_USER_PASSWORD)
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, { currentPassword, newPassword })
  }

  searchUserByEmail(email) {
    const endpoint = this.managerEndpoint(PATH_SEARCH_USER_EMAIL)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint, { params: { email: email } })
  }

  fetchSubscriptionInfo() {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  sendFollowRequest(toUsername) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST)
    const userInfo = storage.getUserInfo()
    const request = { fromUsername: userInfo.username, toUsername: toUsername }
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint, request)
  }

  listIncomingSubscriptionRequests(username) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST) + '/incoming'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  listOutgoingSubscriptionRequests(username) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST) + '/outgoing'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  allowSubscriptionRequest(subscriptionRequest) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST) + '/' + subscriptionRequest.id
    const request = { action: 'Accept' }
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  denySubscriptionRequest(subscriptionRequest) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST) + '/' + subscriptionRequest.id
    const request = { action: 'Reject' }
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  deleteFollowRequest(subscriptionRequest) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION_REQUEST) + '/' + subscriptionRequest.id
    return connectHttpBase.getAuthenticatedHttpClient().delete(endpoint)
  }

  unsubscribeAccount(username) {
    const endpoint = this.managerEndpoint(PATH_SUBSCRIPTION) + '/account/' + username
    return connectHttpBase.getAuthenticatedHttpClient().delete(endpoint)
  }

  updateUserIcon(params) {
    const endpoint = this.managerEndpoint(PATH_USER_ICON)
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, params)
  }

  getStripeInfo() {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/info'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  getInvoicesUpcoming(subscriptionId) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/invoices/upcoming'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  getInvoices(subscriptionId) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/invoices'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  addStripeCreditCard(addCardRequest) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/card'
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint, addCardRequest)
  }

  deleteStripeCreditCard(card) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/card/' + card.id
    return connectHttpBase.getAuthenticatedHttpClient().delete(endpoint)
  }

  selectStripeCreditCard(card) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/card/' + card.id
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint)
  }

  listBillingPlans() {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/plans'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  subscribeStripePlan(request) {
    const endpoint = this.managerEndpoint(V2_PATH_STRIPE) + '/subscription'
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint, request)
  }

  changeStripePlan(request) {
    const endpoint =
      this.managerEndpoint(V2_PATH_STRIPE) + '/subscriptions/' + request.subscriptionId + '/' + request.planId
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint)
  }

  updateCustomerName(request) {
    const endpoint = this.managerEndpoint(V2_PATH_STRIPE) + '/customer/name'
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  updateCustomerEmail(request) {
    const endpoint = this.managerEndpoint(V2_PATH_STRIPE) + '/customer/email'
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  changePaymentMethodTypes(params) {
    const request = {
      addCardRequest: params.addCardRequest,
    }
    const endpoint =
      this.managerEndpoint(V2_PATH_STRIPE) +
      '/subscriptions/payment_method/' +
      params.subscriptionId +
      '/' +
      params.paymentMethodTypes
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, params.addCardRequest)
  }

  unsubscribeStripe(subscriptionId) {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/subscriptions/' + subscriptionId
    return connectHttpBase.getAuthenticatedHttpClient().delete(endpoint)
  }

  listOnlyAgriBusNavi() {
    const endpoint = this.managerEndpoint(PATH_DEVICES) + '/agribusnavi?include=Subscribing'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  updateWebMarker(updateWebMarkerRequest) {
    const username = updateWebMarkerRequest.username
    const deviceId = updateWebMarkerRequest.deviceId
    const endpoint = this.managerEndpoint(PATH_WEB_MARKRES) + '/' + username + '/' + deviceId
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, updateWebMarkerRequest)
  }

  listWebMarkers() {
    const endpoint = this.managerEndpoint(PATH_WEB_MARKRES) + '?include=Subscribing'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  managerEndpoint(path) {
    return ApplicationConfig.getManagerServer() + path
  }

  getUserGroupsInfo() {
    const endpoint = this.managerEndpoint(V2_PATH_USER) + 'groups'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  subscribeStripePlanWithDiscount(params) {
    const endpoint =
      this.managerEndpoint(PATH_STRIPE) + '/plans/' + params.planId + '/subscription/discount/' + params.couponCode
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint)
  }

  getRtkSettingInfo() {
    const endpoint = this.managerEndpoint(V3_PATH_CASTER) + '/setting/rtk'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  listCurrentCasterBaseAndClient() {
    const endpoint = this.managerEndpoint(V3_PATH_CASTER) + '/current'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  updateSystemOfUnits(systemOfUnits) {
    const endpoint = this.managerEndpoint(V3_PATH_USER) + '/units'
    const request = { systemOfUnits }
    return connectHttpBase.getAuthenticatedHttpClient().put(endpoint, request)
  }

  listStripeCoupons() {
    const endpoint = this.managerEndpoint(PATH_STRIPE) + '/coupons'
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }

  /**
   * FireTest
   */
  updateFsMigrated() {
    const endpoint = this.managerEndpoint(V1_FIRETEST)
    return connectHttpBase.getAuthenticatedHttpClient().post(endpoint)
  }

  checkFsMigrated() {
    const endpoint = this.managerEndpoint(V1_FIRETEST)
    return connectHttpBase.getAuthenticatedHttpClient().get(endpoint)
  }
}

const connectManagerClient = new ConnectManagerClient()
export default connectManagerClient
